import azCommon from './az/common.json';
import enCommon from './en/common.json';
import ruCommon from './ru/common.json';

export const LANGUAGES = ['az', 'en', 'ru'] as const;

export type Language = (typeof LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'az';

export type Catalog = Record<string, string>;

export type MessageKey = string;

/**
 * Catalog ownership (ticket 09): `common.json` is owned here, and every later
 * screen ticket adds only `i18n/<locale>/screens/<screen>.json`. The screen
 * files are merged automatically, under both `<screen>.<key>` (collision-free)
 * and the bare key (first screen wins), so no ticket has to edit this file.
 */
const screenModules = import.meta.glob<{ default: Catalog }>('./*/screens/*.json', { eager: true });

function withScreens(locale: Language, common: Catalog): Catalog {
  const catalog: Catalog = { ...common };
  for (const [path, module] of Object.entries(screenModules)) {
    const parts = path.split('/');
    if (parts[1] !== locale) {
      continue;
    }
    const screen = (parts[3] ?? '').replace(/\.json$/, '');
    const entries = (module as { default: Catalog }).default;
    for (const [key, value] of Object.entries(entries)) {
      if (catalog[key] === undefined) {
        catalog[key] = value;
      }
      catalog[`${screen}.${key}`] = value;
    }
  }
  return catalog;
}

export const catalogs: Record<Language, Catalog> = {
  az: withScreens('az', azCommon as Catalog),
  en: withScreens('en', enCommon as Catalog),
  ru: withScreens('ru', ruCommon as Catalog),
};

export function isLanguage(value: string | null | undefined): value is Language {
  return value === 'az' || value === 'en' || value === 'ru';
}

export function translate(language: Language, key: MessageKey): string {
  return catalogs[language][key] ?? key;
}
